import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useApp } from "@/contexts/AppContext";
import { useToast } from "@/hooks/use-toast";
import { CHAT_THEMES, DEFAULT_THEME_ID, getTheme, ChatTheme } from "@/lib/chatThemes";
import {
  MESSAGE_BUBBLES,
  DEFAULT_BUBBLE_ID,
  getBubble,
  MessageBubble,
} from "@/lib/messageBubbles";

// Midnight is the implicit default for every account — always considered owned
// so applying it works even before the user has any purchases recorded.
const IMPLICIT_OWNED: string[] = [DEFAULT_THEME_ID];
const IMPLICIT_OWNED_BUBBLES: string[] = [DEFAULT_BUBBLE_ID];

interface ChatThemeContextType {
  activeThemeId: string;
  activeTheme: ChatTheme;
  ownedThemeIds: string[];
  loading: boolean;
  purchasing: string | null;
  applying: string | null;
  isOwned: (id: string) => boolean;
  purchaseTheme: (id: string) => Promise<{ success: boolean; error?: string }>;
  applyTheme: (id: string) => Promise<{ success: boolean; error?: string }>;
  refresh: () => Promise<void>;
  // Bubbles
  activeBubbleId: string;
  activeBubble: MessageBubble;
  ownedBubbleIds: string[];
  purchasingBubble: string | null;
  applyingBubble: string | null;
  isBubbleOwned: (id: string) => boolean;
  purchaseBubble: (id: string) => Promise<{ success: boolean; error?: string }>;
  applyBubble: (id: string) => Promise<{ success: boolean; error?: string }>;
}

const ChatThemeContext = createContext<ChatThemeContextType | undefined>(undefined);

export const ChatThemeProvider = ({ children }: { children: ReactNode }) => {
  const { user, refreshBalance } = useApp();
  const { toast } = useToast();
  const [activeThemeId, setActiveThemeId] = useState<string>(DEFAULT_THEME_ID);
  const [ownedThemeIds, setOwnedThemeIds] = useState<string[]>(IMPLICIT_OWNED);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [applying, setApplying] = useState<string | null>(null);
  const [activeBubbleId, setActiveBubbleId] = useState<string>(DEFAULT_BUBBLE_ID);
  const [ownedBubbleIds, setOwnedBubbleIds] = useState<string[]>(IMPLICIT_OWNED_BUBBLES);
  const [purchasingBubble, setPurchasingBubble] = useState<string | null>(null);
  const [applyingBubble, setApplyingBubble] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [{ data: profile }, { data: purchases }] = await Promise.all([
        supabase.from("profiles").select("active_chat_theme, active_bubble").eq("user_id", user.id).maybeSingle(),
        supabase.from("user_purchases").select("item_id, item_type").eq("user_id", user.id).in("item_type", ["chat_theme", "message_bubble"]),
      ]);
      const owned = new Set<string>(IMPLICIT_OWNED);
      const ownedBubbles = new Set<string>(IMPLICIT_OWNED_BUBBLES);
      (purchases ?? []).forEach((p: any) => {
        if (p.item_type === "chat_theme") owned.add(p.item_id);
        if (p.item_type === "message_bubble") ownedBubbles.add(p.item_id);
      });
      setOwnedThemeIds(Array.from(owned));
      setOwnedBubbleIds(Array.from(ownedBubbles));
      setActiveThemeId((profile as any)?.active_chat_theme ?? DEFAULT_THEME_ID);
      setActiveBubbleId((profile as any)?.active_bubble ?? DEFAULT_BUBBLE_ID);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) void refresh();
    else {
      setActiveThemeId(DEFAULT_THEME_ID);
      setOwnedThemeIds(IMPLICIT_OWNED);
      setActiveBubbleId(DEFAULT_BUBBLE_ID);
      setOwnedBubbleIds(IMPLICIT_OWNED_BUBBLES);
    }
  }, [user?.id, refresh]);

  const isOwned = useCallback((id: string) => ownedThemeIds.includes(id), [ownedThemeIds]);
  const isBubbleOwned = useCallback((id: string) => ownedBubbleIds.includes(id), [ownedBubbleIds]);

  const purchaseBubble = useCallback(
    async (id: string) => {
      const bubble = MESSAGE_BUBBLES.find((b) => b.id === id);
      if (!bubble) return { success: false, error: "Bubble not found" };
      if (isBubbleOwned(id)) return { success: true };
      setPurchasingBubble(id);
      try {
        const { data, error } = await supabase.rpc("purchase_message_bubble" as any, {
          p_bubble_id: id,
          p_price: bubble.price,
        });
        if (error) throw error;
        const result = data as any;
        if (!result?.success) {
          const err = result?.error ?? "purchase_failed";
          if (err === "insufficient_coins") {
            toast({ title: "Not enough coins", description: `You need ${bubble.price} coins.`, variant: "destructive" });
          } else {
            toast({ title: "Purchase failed", description: err, variant: "destructive" });
          }
          return { success: false, error: err };
        }
        setOwnedBubbleIds((prev) => Array.from(new Set([...prev, id])));
        await refreshBalance();
        toast({ title: "Bubble unlocked! 💬", description: bubble.name });
        return { success: true };
      } catch (e: any) {
        toast({ title: "Purchase failed", description: e.message, variant: "destructive" });
        return { success: false, error: e.message };
      } finally {
        setPurchasingBubble(null);
      }
    },
    [isBubbleOwned, refreshBalance, toast]
  );

  const applyBubble = useCallback(
    async (id: string) => {
      if (!isBubbleOwned(id)) return { success: false, error: "not_owned" };
      setApplyingBubble(id);
      const prev = activeBubbleId;
      setActiveBubbleId(id);
      try {
        const { data, error } = await supabase.rpc("set_active_message_bubble" as any, { p_bubble_id: id });
        if (error) throw error;
        const result = data as any;
        if (!result?.success) {
          setActiveBubbleId(prev);
          return { success: false, error: result?.error ?? "apply_failed" };
        }
        return { success: true };
      } catch (e: any) {
        setActiveBubbleId(prev);
        toast({ title: "Failed to apply bubble", description: e.message, variant: "destructive" });
        return { success: false, error: e.message };
      } finally {
        setApplyingBubble(null);
      }
    },
    [activeBubbleId, isBubbleOwned, toast]
  );

  const purchaseTheme = useCallback(
    async (id: string) => {
      const theme = CHAT_THEMES.find((t) => t.id === id);
      if (!theme) return { success: false, error: "Theme not found" };
      if (isOwned(id)) return { success: true };
      setPurchasing(id);
      try {
        const { data, error } = await supabase.rpc("purchase_chat_theme", {
          p_theme_id: id,
          p_price: theme.price,
        } as any);
        if (error) throw error;
        const result = data as any;
        if (!result?.success) {
          const err = result?.error ?? "purchase_failed";
          if (err === "insufficient_coins") {
            toast({ title: "Not enough coins", description: `You need ${theme.price} coins.`, variant: "destructive" });
          } else {
            toast({ title: "Purchase failed", description: err, variant: "destructive" });
          }
          return { success: false, error: err };
        }
        setOwnedThemeIds((prev) => Array.from(new Set([...prev, id])));
        await refreshBalance();
        toast({ title: "Theme unlocked! 🎨", description: theme.name });
        return { success: true };
      } catch (e: any) {
        toast({ title: "Purchase failed", description: e.message, variant: "destructive" });
        return { success: false, error: e.message };
      } finally {
        setPurchasing(null);
      }
    },
    [isOwned, refreshBalance, toast]
  );

  const applyTheme = useCallback(
    async (id: string) => {
      if (!isOwned(id)) return { success: false, error: "not_owned" };
      setApplying(id);
      const prev = activeThemeId;
      setActiveThemeId(id); // optimistic
      try {
        const { data, error } = await supabase.rpc("set_active_chat_theme", { p_theme_id: id });
        if (error) throw error;
        const result = data as any;
        if (!result?.success) {
          setActiveThemeId(prev);
          return { success: false, error: result?.error ?? "apply_failed" };
        }
        return { success: true };
      } catch (e: any) {
        setActiveThemeId(prev);
        toast({ title: "Failed to apply theme", description: e.message, variant: "destructive" });
        return { success: false, error: e.message };
      } finally {
        setApplying(null);
      }
    },
    [activeThemeId, isOwned, toast]
  );

  return (
    <ChatThemeContext.Provider
      value={{
        activeThemeId,
        activeTheme: getTheme(activeThemeId),
        ownedThemeIds,
        loading,
        purchasing,
        applying,
        isOwned,
        purchaseTheme,
        applyTheme,
        refresh,
        activeBubbleId,
        activeBubble: getBubble(activeBubbleId),
        ownedBubbleIds,
        purchasingBubble,
        applyingBubble,
        isBubbleOwned,
        purchaseBubble,
        applyBubble,
      }}
    >
      {children}
    </ChatThemeContext.Provider>
  );
};

export const useChatTheme = () => {
  const ctx = useContext(ChatThemeContext);
  if (!ctx) throw new Error("useChatTheme must be used within ChatThemeProvider");
  return ctx;
};